import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ApiKeyManagement = () => {
  const [selectedEnvironment, setSelectedEnvironment] = useState('production');
  const [newKeyName, setNewKeyName] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);

  const environmentOptions = [
    { value: 'production', label: 'Production Environment' },
    { value: 'sandbox', label: 'Sandbox Environment' },
    { value: 'development', label: 'Development Environment' }
  ];

  const apiKeys = [
    {
      id: 1,
      name: "Production API Key",
      key: "ipsos_prod_••••••••••••••••••••••••••••••••••••••••",
      environment: "production",
      created: "2024-10-15T10:30:00Z",
      lastUsed: "2024-10-29T14:22:00Z",
      status: "active",
      permissions: ["read", "write", "admin"]
    },
    {
      id: 2,
      name: "Sandbox Testing Key",
      key: "ipsos_sand_••••••••••••••••••••••••••••••••••••••••",
      environment: "sandbox",
      created: "2024-10-20T09:15:00Z",
      lastUsed: "2024-10-28T16:45:00Z",
      status: "active",
      permissions: ["read", "write"]
    },
    {
      id: 3,
      name: "Development Key",
      key: "ipsos_dev_••••••••••••••••••••••••••••••••••••••••",
      environment: "development",
      created: "2024-10-25T11:20:00Z",
      lastUsed: "2024-10-29T12:10:00Z",
      status: "inactive",
      permissions: ["read"]
    }
  ];

  const filteredKeys = apiKeys?.filter(key => key?.environment === selectedEnvironment);

  const handleCreateKey = () => {
    if (newKeyName?.trim()) {
      // Mock key creation logic
      setNewKeyName('');
      setShowCreateForm(false);
    }
  };

  const copyToClipboard = (key) => {
    navigator.clipboard?.writeText(key);
  };

  const revokeKey = (keyId) => {
    // Mock revoke logic
    console.log('Revoking key:', keyId);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground">API Key Management</h3>
          <p className="text-sm text-text-secondary mt-1">
            Manage your API keys for secure access to Ipsos Insight services
          </p>
        </div>
        <Button
          variant="default"
          iconName="Plus"
          iconPosition="left"
          onClick={() => setShowCreateForm(true)}
        >
          Generate New Key
        </Button>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1">
          <Select
            label="Environment"
            description="Select environment to view keys"
            options={environmentOptions}
            value={selectedEnvironment}
            onChange={setSelectedEnvironment}
          />
        </div>
      </div>
      {showCreateForm && (
        <div className="bg-card border border-border rounded-lg p-6 animate-fade-in">
          <h4 className="text-md font-semibold text-card-foreground mb-4">Create New API Key</h4>
          <div className="space-y-4">
            <Input
              label="Key Name"
              placeholder="Enter a descriptive name for this key"
              value={newKeyName}
              onChange={(e) => setNewKeyName(e?.target?.value)}
              required
            />
            <div className="flex items-center gap-3">
              <Button variant="default" onClick={handleCreateKey}>
                Generate Key
              </Button>
              <Button variant="outline" onClick={() => setShowCreateForm(false)}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-4">
        {filteredKeys?.length === 0 ? (
          <div className="text-center py-12 bg-muted rounded-lg">
            <Icon name="Key" size={48} className="text-muted-foreground mx-auto mb-4" />
            <h4 className="text-lg font-medium text-foreground mb-2">No API Keys Found</h4>
            <p className="text-text-secondary mb-4">
              No API keys exist for the {selectedEnvironment} environment
            </p>
            <Button variant="default" onClick={() => setShowCreateForm(true)}>
              Create First Key
            </Button>
          </div>
        ) : (
          filteredKeys?.map((apiKey) => (
            <div key={apiKey?.id} className="bg-card border border-border rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-md font-semibold text-card-foreground">{apiKey?.name}</h4>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      apiKey?.status === 'active' ?'bg-success/10 text-success' :'bg-muted text-muted-foreground'
                    }`}>
                      {apiKey?.status}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-2 mb-3">
                    <code className="text-sm font-mono bg-muted px-3 py-1 rounded text-muted-foreground">
                      {apiKey?.key}
                    </code>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(apiKey?.key)}
                    >
                      <Icon name="Copy" size={16} />
                    </Button>
                  </div>
                  
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-text-secondary">Created:</span>
                      <p className="text-card-foreground font-medium">
                        {new Date(apiKey.created)?.toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Last Used:</span>
                      <p className="text-card-foreground font-medium">
                        {new Date(apiKey.lastUsed)?.toLocaleDateString()}
                      </p>
                    </div>
                    <div>
                      <span className="text-text-secondary">Permissions:</span>
                      <div className="flex flex-wrap gap-1 mt-1">
                        {apiKey?.permissions?.map((permission) => (
                          <span
                            key={permission}
                            className="px-2 py-0.5 text-xs bg-primary/10 text-primary rounded"
                          >
                            {permission}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm">
                    <Icon name="Edit" size={16} />
                  </Button>
                  <Button 
                    variant="destructive" 
                    size="sm"
                    onClick={() => revokeKey(apiKey?.id)}
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ApiKeyManagement;